import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, ShieldCheck, CheckCircle, Award } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { eventTeamApiService, EventTeamApiError } from '../../services/eventTeamApi';
import type { EventTeamMember, GrantPrefixResponse, Prefix, EventPrefixRequirement } from '../../types/eventTeam';
import { PrefixBadge } from './PrefixBadge';
import { PlayerProfile } from '../PlayerProfile';
import { PlayerSearchField, type PlayerSelection } from './PlayerSearchField';
import { cn } from '../../utils/cn';

export interface GrantPrefixModalProps {
  isOpen: boolean;
  prefix: Prefix;
  requirement?: EventPrefixRequirement | null;
  /** Preselect the grantee (from an eligibility row or player lookup). */
  presetUuid?: string;
  onClose: () => void;
  /** Fired on ANY successful grant, including `already_held: true` (guide §2). */
  onSuccess: (result: GrantPrefixResponse) => void;
}

/**
 * Apply a prefix to a player — writes the LIVE `userPrefix` row (event manager only).
 * Handles the full UX flow from guide §2: `already_held` distinctly, `points_entry`
 * spend toast, and the 422 "player has no user record" message verbatim.
 */
export function GrantPrefixModal({
  isOpen,
  prefix,
  requirement,
  presetUuid,
  onClose,
  onSuccess,
}: GrantPrefixModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [members, setMembers] = useState<EventTeamMember[]>([]);
  const [memberUuid, setMemberUuid] = useState('');
  const [customPlayer, setCustomPlayer] = useState<PlayerSelection | null>(null);
  const [useCustomUuid, setUseCustomUuid] = useState(false);
  const [spendPoints, setSpendPoints] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GrantPrefixResponse | null>(null);

  const cost = requirement?.points_cost ?? null;

  useEffect(() => {
    if (!isOpen) return;
    setMemberUuid(presetUuid ?? '');
    setCustomPlayer(null);
    setUseCustomUuid(false);
    setSpendPoints(false);
    setNotes('');
    setError(null);
    setResult(null);
    eventTeamApiService
      .listTeamMembers({ limit: 200 })
      .then((res) => setMembers(res.results))
      .catch(() => setMembers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const targetUuid = useCustomUuid ? (customPlayer?.uuid ?? '') : memberUuid || presetUuid || '';

  const handleSubmit = async () => {
    if (!targetUuid) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await eventTeamApiService.grantPrefix(prefix.id, {
        member_uuid: targetUuid,
        notes: notes.trim() || null,
        spend_points: spendPoints,
      });
      setResult(res);
      onSuccess(res);
    } catch (err) {
      if (err instanceof EventTeamApiError && err.status === 422) {
        // Actionable message from the server: the player has no `user` record.
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to grant prefix');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-glass-400 hover:text-white p-2"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-1 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-green-400" />
          Grant prefix
        </h2>
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <PrefixBadge prefixJson={prefix.prefix_json} name={prefix.name} size="md" />
          <span className="text-xs text-glass-400">
            {prefix.name} · {prefix.category}
          </span>
        </div>

        {result ? (
          <div className="space-y-4">
            <div
              className={cn(
                'flex items-start gap-2 p-3 rounded-lg border',
                result.already_held ? 'bg-amber-500/10 border-amber-500/20' : 'bg-green-500/10 border-green-500/20',
              )}
            >
              <CheckCircle className={cn('w-5 h-5 flex-shrink-0', result.already_held ? 'text-amber-400' : 'text-green-400')} />
              <div>
                <p className={cn('text-sm font-medium', result.already_held ? 'text-amber-300' : 'text-green-300')}>
                  {result.already_held ? 'Already held. Grant recorded in history.' : 'Prefix granted.'}
                </p>
                {result.points_entry && (
                  <p className="text-xs text-violet-300 mt-1 inline-flex items-center gap-1">
                    <Award className="w-3.5 h-3.5" />
                    {result.points_entry.delta} points
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={onClose} className="glass-button text-sm">
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span className="text-sm text-red-300">{error}</span>
              </div>
            )}

            <div>
              <label className="text-sm text-glass-300 mb-1 block">Player *</label>
              {presetUuid && !useCustomUuid ? (
                <PlayerProfile uuid={presetUuid} showUuid={false} />
              ) : useCustomUuid ? (
                <PlayerSearchField value={customPlayer} onChange={setCustomPlayer} autoFocus />
              ) : (
                <select
                  value={memberUuid}
                  onChange={(e) => setMemberUuid(e.target.value)}
                  className="glass-input w-full text-sm"
                >
                  <option value="">Select roster member…</option>
                  {members.map((m) => (
                    <option key={m.minecraft_uuid} value={m.minecraft_uuid}>
                      {m.display_name} ({m.role})
                    </option>
                  ))}
                </select>
              )}
              <button
                type="button"
                onClick={() => setUseCustomUuid(!useCustomUuid)}
                className="text-[11px] text-accent-300 hover:text-accent-200 mt-1"
              >
                {useCustomUuid ? 'Pick from the roster' : 'Search any player'}
              </button>
            </div>

            <label className="flex items-center gap-2 text-sm text-glass-300 cursor-pointer">
              <input
                type="checkbox"
                checked={spendPoints}
                onChange={(e) => setSpendPoints(e.target.checked)}
                className="rounded border-glass-200/30"
              />
              <Award className="w-4 h-4 text-violet-400" />
              {cost != null && cost > 0 ? `Spend ${cost} points` : 'Spend points'}
            </label>

            <div>
              <label className="text-sm text-glass-300 mb-1 block">Notes</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="glass-input w-full text-sm"
                placeholder="e.g. Earned at Heist 2026"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="glass-button-secondary text-sm">
                Cancel
              </button>
              <button
                onClick={() => void handleSubmit()}
                disabled={submitting || !targetUuid}
                className="glass-button text-sm inline-flex items-center gap-1.5"
              >
                {submitting && <Loader className="w-4 h-4 animate-spin" />}
                Grant prefix
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
